import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  ButtonText,
  ContractIcon,
  ExpandIcon,
  Lightbox,
  OverlayTriggerTooltip,
  TableData,
} from '@utilities';

import {
  COMPARE_BY_ACCESSOR,
  getColumnAccessor,
  OPTION_LABEL_ACCESSORS,
} from '@api/survey_dashboard/survey_responses/utilities/columns';

import {
  getDisplayText,
  getOptionLabel,
  getQuestionOptionId,
  getThumbnailRegionId,
  parseOptionId,
} from './utilities/helpers';
import CollapsibleTable from '../CollapsibleTable';

import styles from './_index.module.scss';

const EMPTY_ARRAY = [];
const ROW_ID_DELIMITER = '\u001F';

const OptionImageTable = ({
  actionButtons = EMPTY_ARRAY,
  columns = EMPTY_ARRAY,
  compareByGroups = EMPTY_ARRAY,
  data = EMPTY_ARRAY,
  questionOptions = EMPTY_ARRAY,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedOptionIds, setExpandedOptionIds] = useState(new Set());
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const { lightboxImages, optionImageMap } = useMemo(() => {
    const map = new Map();
    const images = [];

    questionOptions.forEach((option, optionIndex) => {
      const optionId = getQuestionOptionId(option);
      const imageUrl = option?.image?.cdnUrl;

      if (optionId === null || !imageUrl || map.has(optionId)) return;

      const label = getOptionLabel({
        fallbackLabel: `Option ${optionIndex + 1}`,
        option,
      });
      const image = {
        alt: label,
        caption: label,
        src: imageUrl,
      };

      map.set(optionId, {
        ...image,
        imageIndex: images.length,
      });
      images.push(image);
    });

    return { lightboxImages: images, optionImageMap: map };
  }, [questionOptions]);

  const availableOptionIds = useMemo(() => {
    const uniqueOptionIds = new Set();

    data.forEach(({ option_id: optionId }) => {
      const parsedOptionId = parseOptionId(optionId);
      if (parsedOptionId === null || !optionImageMap.has(parsedOptionId)) return;
      uniqueOptionIds.add(parsedOptionId);
    });

    return Array.from(uniqueOptionIds);
  }, [data, optionImageMap]);

  const isAllExpanded = useMemo(() => {
    if (!availableOptionIds.length) return false;
    return availableOptionIds.every((optionId) => expandedOptionIds.has(optionId));
  }, [availableOptionIds, expandedOptionIds]);

  useEffect(() => {
    const availableOptionIdsSet = new Set(availableOptionIds);

    setExpandedOptionIds((previousState) => {
      return new Set(
        Array.from(previousState).filter((optionId) => availableOptionIdsSet.has(optionId))
      );
    });
  }, [availableOptionIds]);

  const stopEvent = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const onOpenLightbox = useCallback(
    (optionId) => {
      const option = optionImageMap.get(optionId);
      if (!option) return;

      setCurrentImageIndex(option.imageIndex);
      setIsLightboxOpen(true);
    },
    [optionImageMap]
  );

  const onToggleAllOptionImages = () => {
    setExpandedOptionIds(isAllExpanded ? new Set() : new Set(availableOptionIds));
  };

  const onToggleOptionImage = useCallback((optionId) => {
    setExpandedOptionIds((previousState) => {
      const updatedState = new Set(previousState);

      if (updatedState.has(optionId)) {
        updatedState.delete(optionId);
      } else {
        updatedState.add(optionId);
      }

      return updatedState;
    });
  }, []);

  const optionLabelAccessor = useMemo(() => {
    return columns
      .map((column) => getColumnAccessor(column))
      .find((accessor) => OPTION_LABEL_ACCESSORS.includes(accessor));
  }, [columns]);

  /**
   * Accessor grouping consecutive rows under one option label, or null when each row is its
   * own option. Read off the column rather than assuming `option_id` so the rank and max-diff
   * tables, which send one row per option and no grouping, are unaffected.
   */
  const rowGroupAccessor = useMemo(
    () => columns.find((column) => column.rowSpanBy)?.rowSpanBy ?? null,
    [columns]
  );

  /**
   * Indexes of the rows that open a group. The image toggle renders on those alone: the rest of
   * the group blanks its label, so a toggle there would be an invisible duplicate control and
   * would repeat the expanded thumbnail once per compare-by segment.
   */
  const groupStartRowIndexes = useMemo(() => {
    if (!rowGroupAccessor) return null;

    return new Set(
      data.reduce((startIndexes, row, index) => {
        if (index === 0 || data[index - 1]?.[rowGroupAccessor] !== row?.[rowGroupAccessor]) {
          startIndexes.push(index);
        }
        return startIndexes;
      }, [])
    );
  }, [data, rowGroupAccessor]);

  const tableColumns = useMemo(() => {
    if (!optionLabelAccessor || optionImageMap.size === 0) return columns;

    return columns.map((column) => {
      const accessor = getColumnAccessor(column);
      if (accessor !== optionLabelAccessor) return column;

      const originalCell = column.Cell;

      return {
        ...column,
        Cell: (cellProps) => {
          const labelText = getDisplayText(cellProps?.value);
          const optionId = parseOptionId(cellProps?.row?.original?.option_id);
          const optionImage = optionId === null ? null : optionImageMap.get(optionId);
          const isGroupRepeat =
            groupStartRowIndexes !== null && !groupStartRowIndexes.has(cellProps?.row?.index);

          if (!optionImage || isGroupRepeat) {
            return originalCell ? originalCell(cellProps) : labelText;
          }

          const isExpanded = expandedOptionIds.has(optionId);
          const thumbnailRegionId = getThumbnailRegionId({
            optionId,
            rowId: cellProps?.row?.id ?? cellProps?.row?.index ?? optionId,
          });
          const toggleLabel = `${isExpanded ? 'Hide' : 'Show'} image for ${labelText}`;

          return (
            <div className={styles['option-image-cell']}>
              <ButtonText
                aria-controls={thumbnailRegionId}
                aria-expanded={isExpanded}
                aria-label={toggleLabel}
                className={styles['option-image-toggle']}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleOptionImage(optionId);
                }}
                onMouseDown={stopEvent}
                type="button"
              >
                {labelText}
              </ButtonText>
              {isExpanded && (
                <div
                  aria-label={`Image preview for ${labelText}`}
                  className={styles['option-image-thumbnail-region']}
                  id={thumbnailRegionId}
                  role="region"
                >
                  <button
                    aria-label={`Open image for ${labelText}`}
                    className={styles['option-image-thumbnail-button']}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenLightbox(optionId);
                    }}
                    onMouseDown={stopEvent}
                    type="button"
                  >
                    <img
                      alt={optionImage.alt}
                      className={styles['option-image-thumbnail']}
                      src={optionImage.src}
                    />
                  </button>
                </div>
              )}
            </div>
          );
        },
        meta: {
          ...(column.meta || {}),
          allowCellWrap: true,
          isOptionLabel: true,
        },
      };
    });
  }, [
    columns,
    expandedOptionIds,
    groupStartRowIndexes,
    onOpenLightbox,
    onToggleOptionImage,
    optionImageMap,
    optionLabelAccessor,
    stopEvent,
  ]);

  const getRowId = useCallback(
    (row, index) => {
      const optionKey = row?.option_id ?? (rowGroupAccessor ? row?.[rowGroupAccessor] : undefined);
      const labelKey = optionLabelAccessor ? row?.[optionLabelAccessor] : undefined;
      const segmentKey = row?.[COMPARE_BY_ACCESSOR];

      const parts = [optionKey, labelKey, segmentKey].filter((part) => part != null);
      if (parts.length) return parts.join(ROW_ID_DELIMITER);
      const rowValues = row ? Object.values(row).filter((value) => value != null) : [];
      return rowValues.length ? rowValues.join(ROW_ID_DELIMITER) : String(index);
    },
    [optionLabelAccessor, rowGroupAccessor]
  );

  const columnsWithCompareByDots = useMemo(
    () =>
      tableColumns.map((column) => {
        if (getColumnAccessor(column) !== COMPARE_BY_ACCESSOR) return column;

        const originalCell = column.Cell;

        return {
          ...column,
          Cell: (cellProps) => {
            const color = compareByGroups.find(({ id, name }) =>
              [id, name].includes(cellProps?.value)
            )?.color;
            const content = originalCell
              ? originalCell(cellProps)
              : getDisplayText(cellProps?.value);

            return (
              <div className={styles['compare-by-cell']}>
                {color && (
                  <span
                    className={styles['compare-by-dot']}
                    data-testid="compare-by-dot"
                    style={{ backgroundColor: color }}
                  />
                )}
                {content}
              </div>
            );
          },
        };
      }),
    [tableColumns, compareByGroups]
  );

  const hasOptionImages = availableOptionIds.length > 0;
  const toggleImagesButtonText = isAllExpanded ? 'Hide All Images' : 'Show All Images';

  const tableActionButtons = hasOptionImages
    ? [
        ...actionButtons,
        <OverlayTriggerTooltip content={toggleImagesButtonText} key="option-image-expand-toggle">
          <Button
            aria-label={toggleImagesButtonText}
            className={styles['option-image-table-toggle-button']}
            icon={
              isAllExpanded ? (
                <ContractIcon className={styles['option-image-table-toggle-icon']} />
              ) : (
                <ExpandIcon className={styles['option-image-table-toggle-icon']} />
              )
            }
            onClick={onToggleAllOptionImages}
            text={toggleImagesButtonText}
            variant="secondary"
          />
        </OverlayTriggerTooltip>,
      ]
    : actionButtons;

  return (
    <>
      <CollapsibleTable rowCount={data.length}>
        <TableData
          actionButtons={tableActionButtons}
          columns={columnsWithCompareByDots}
          data={data}
          getRowId={getRowId}
        />
      </CollapsibleTable>
      {lightboxImages.length > 0 && (
        <Lightbox
          currentImageIndex={currentImageIndex}
          images={lightboxImages}
          isActive={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}
    </>
  );
};

OptionImageTable.propTypes = {
  actionButtons: PropTypes.arrayOf(PropTypes.node),
  columns: PropTypes.arrayOf(PropTypes.object),
  compareByGroups: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
    })
  ),
  data: PropTypes.arrayOf(PropTypes.object),
  questionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      image: PropTypes.shape({
        cdnUrl: PropTypes.string,
      }),
      optionSourceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      text: PropTypes.string,
      title: PropTypes.string,
      value: PropTypes.string,
    })
  ),
};

export default OptionImageTable;
